import { api, handleApiError } from '../client'
import type {
  TemplatePermission,
  TemplatePermissionsDetail,
  EffectivePermission,
  PermissionAuditLog,
  GrantPermissionRequest,
  UpdatePermissionRequest,
  PermissionLevel,
} from '../types'

export const templatePermissionService = {
  /**
   * Gets all permissions for a template
   */
  async getPermissions(templateId: string): Promise<TemplatePermissionsDetail> {
    try {
      const response = await api.get<TemplatePermissionsDetail>(
        `/v1/templates/${templateId}/permissions`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the effective permission for the current user on a template
   */
  async getEffectivePermission(templateId: string): Promise<EffectivePermission> {
    try {
      const response = await api.get<EffectivePermission>(
        `/v1/templates/${templateId}/permissions/effective`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Grants a new permission on a template
   */
  async grantPermission(
    templateId: string,
    request: GrantPermissionRequest
  ): Promise<TemplatePermission> {
    try {
      const response = await api.post<TemplatePermission>(
        `/v1/templates/${templateId}/permissions`,
        request
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing permission
   */
  async updatePermission(
    templateId: string,
    permissionId: string,
    request: UpdatePermissionRequest
  ): Promise<TemplatePermission> {
    try {
      const response = await api.put<TemplatePermission>(
        `/v1/templates/${templateId}/permissions/${permissionId}`,
        request
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Revokes a permission
   */
  async revokePermission(templateId: string, permissionId: string): Promise<void> {
    try {
      await api.delete(`/v1/templates/${templateId}/permissions/${permissionId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Bulk update permissions for a template
   */
  async bulkUpdatePermissions(
    templateId: string,
    permissions: GrantPermissionRequest[]
  ): Promise<TemplatePermission[]> {
    try {
      const response = await api.post<TemplatePermission[]>(
        `/v1/templates/${templateId}/permissions/bulk`,
        { permissions }
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Syncs permissions from the parent hub
   */
  async syncFromHub(templateId: string): Promise<TemplatePermission[]> {
    try {
      const response = await api.post<TemplatePermission[]>(
        `/v1/templates/${templateId}/permissions/sync-from-hub`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the audit log for permission changes
   */
  async getAuditLog(templateId: string): Promise<PermissionAuditLog[]> {
    try {
      const response = await api.get<PermissionAuditLog[]>(
        `/v1/templates/${templateId}/permissions/audit`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Checks if the current user has a specific permission level on a template
   */
  async checkPermission(templateId: string, level: PermissionLevel): Promise<boolean> {
    try {
      const response = await api.get<{ hasPermission: boolean }>(
        `/v1/templates/${templateId}/permissions/check`,
        { params: { level } }
      )
      return response.data.hasPermission
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Toggles hub inheritance for a template
   */
  async toggleHubInheritance(templateId: string, inherit: boolean): Promise<void> {
    try {
      await api.post(`/v1/templates/${templateId}/permissions/inheritance`, { inherit })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
